import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { NotificationManager } from 'react-notifications';
import { useMsal } from '@azure/msal-react';
import useFetchSensorPurchaseOrders from './useFetchSensorPurchaseOrders';
import { msalConfig } from '../auth/authConfig';
import useUserStore from '../store/userStore';
import { errorHandler } from '../utils/helpers';
import { acquireToken } from '../utils/auth';

interface EditSensorPurchaseOrderRequest {
  id: string | number;
  token: string | null;
  setError: (data: any) => any;
  setLoading: (data: any) => any;
  fetchSensorPurchaseOrders: () => void;
  data: EditPurchaseOrderDTO;
};

const editSensorPurchaseOrderRequest = async ({
    token,
    setError,
    setLoading,
    fetchSensorPurchaseOrders,
    data,
    id,
  }: EditSensorPurchaseOrderRequest) => {
  try {
    const response = await axios.put(`${window.__env.REACT_APP_API_URL || process.env.REACT_APP_API_URL}/Sensor/PurchaseOrder/${id}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json, text/plain, */*',
      },
    });
    if (response.status === 200) {
      NotificationManager.success('Sensor Purchase Order is successfully edited');
    }
  } catch (error) {
    errorHandler(error, setError);
  } finally {
    fetchSensorPurchaseOrders();
    setLoading(false);
  }
};

interface EditPurchaseOrderDTO { description: string, number: string, userId?: string | number };

interface EditSensorPurchaseOrder {
  editSensorPurchaseOrder: (id: string | number, formData: EditPurchaseOrderDTO) => void;
  loading: boolean;
  error: Error | null;
};

const useEditSensorPurchaseOrder = (): EditSensorPurchaseOrder => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const { fetchSensorPurchaseOrders } = useFetchSensorPurchaseOrders();
  
  const { user } = useUserStore();

  const editSensorPurchaseOrder = async (id, formData: EditPurchaseOrderDTO) => {
    if (accounts.length > 0) {
      const accessTokenRequest = {
        scopes: [`${msalConfig.auth.clientId}/.default`],
        account: accounts[0],
      };

      if (!user) return navigate('/sign-in');

      const data: EditPurchaseOrderDTO = {
        ...formData,
        userId: user.id,
      };

      try {
        const accessToken = await acquireToken(instance, accessTokenRequest);
        editSensorPurchaseOrderRequest({
          id,
          token: accessToken,
          setError,
          fetchSensorPurchaseOrders,
          setLoading,
          data,
        });
      } catch (error) {
        errorHandler(error, setError);
      }
    } else {
      navigate('/sign-in');
    }
  }

  return { editSensorPurchaseOrder, loading, error };
};

export default useEditSensorPurchaseOrder;
